import dayjs from 'dayjs';
import i18n from 'i18next';
import { MILLISECONDS_IN_SECOND } from 'utils/constants/dateInfo/time-numbers';
import { PeriodNames } from 'utils/constants/sportsbook/period-names';
import {
  ARCHERY_H2H_GAME_STATUSES,
  H2H_COMPOUND_ARCHERY_MATCH_STATE_IN_PLAY,
  H2H_COMPOUND_ARCHERY_MATCH_STATE_NOT_STARTED,
  PISTOL_H2H_SHOOT_OFF_SET_NUMBER
} from 'utils/constants/sportsbook/pistol-and-archery-shootings';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { dateFormat } from 'utils/date-manipulation/date-format';
import { ESPORTS_ADDITIONAL_DATA } from 'utils/helpers/moba-games-statistics-options';
import {
  GameInfo,
  GameInfoAdditionalData
} from 'interfaces/sportsbook-data-levels';
import {
  ArcheryH2HSportAlias,
  CompoundArcherySportAlias
} from '../constants/sportsbook/sport-list';

type Props = {
  current_game_state: string;
  current_game_time: string;
  additional_data?: GameInfoAdditionalData;
};

const Leaderboards = {
  Place1st: 1,
  Place2nd: 2,
  Place3rd: 3
};

const TRASNSLATION_KEYS_BY_GAME_STATE: Record<string, string> = {
  'Half End': 'halfEnd',
  'Quarter End': 'quarterEnd'
};

const gamesWithNewData = [ArcheryH2HSportAlias, CompoundArcherySportAlias];

export const getGameCurrentState = (
  {
    current_game_state: gState,
    current_game_time: gTime,
    additional_data: additionalData
  }: Props | GameInfo,
  currentSport = '',
  compactView = false,
  shootOffSet = 0
): string => {
  if (
    gamesWithNewData.includes(currentSport) &&
    (additionalData?.matchState ||
      additionalData?.matchState ===
        H2H_COMPOUND_ARCHERY_MATCH_STATE_NOT_STARTED)
  ) {
    if (
      additionalData?.matchState === H2H_COMPOUND_ARCHERY_MATCH_STATE_IN_PLAY &&
      additionalData?.currentSet
    ) {
      gState = `set${additionalData.currentSet}`;
    } else {
      gState = ARCHERY_H2H_GAME_STATUSES[additionalData?.matchState];
    }
  }

  switch (true) {
    case gState === 'Half Time':
      return compactView
        ? i18n.t(`sportsbook.halfTimeShort`)
        : i18n.t(`sportsbook.halfTime`);

    case ['Half End', 'Quarter End'].includes(gState as string) &&
      currentSport === 'Basketball': {
      return i18n.t(
        `sportsbook.${TRASNSLATION_KEYS_BY_GAME_STATE[gState as string]}`
      );
    }

    case gState === 'timeout':
      return compactView
        ? i18n.t(`sportsbook.timeoutShort`)
        : i18n.t(`sportsbook.timeout`);

    case gState === 'notstarted':
      return compactView ? "0'" : i18n.t(`sportsbook.notStarted`);

    case gState === 'finished':
      return compactView
        ? i18n.t(`sportsbook.finishedShort`)
        : i18n.t(`sportsbook.finished`);

    case gTime && !!gState && currentSport === 'Soccer' && compactView:
      return `${gTime}'`;

    case gTime && !!gState && compactView:
      return `${getPeriodName(
        currentSport,
        gState as string,
        compactView,
        shootOffSet
      )}`;

    case gTime && !!gState:
      return `${getPeriodName(
        currentSport,
        gState as string,
        compactView,
        shootOffSet
      )} ${gTime}'`;

    case !!gTime:
      return `${gTime}'`;

    case !!gState:
      return getPeriodName(
        currentSport,
        gState as string,
        compactView,
        shootOffSet
      );

    case !gState && !gTime:
      return compactView ? '-' : '';
  }

  return '';
};

export function getPeriodName(
  currentSport: string,
  gState: string,
  compactView?: boolean,
  shootOffSet?: number
): string {
  const initialPeriodName = 'set';
  const defaultPeriodName = 'Set';
  const periodName: boolean | string = PeriodNames[currentSport]
    ? PeriodNames[currentSport]
    : defaultPeriodName;

  const periodNumber = +gState.replace(initialPeriodName, '');

  if (isNaN(periodNumber)) {
    return gState.replace(initialPeriodName, '');
  }

  if (
    (currentSport === 'PistolHead2Head' &&
      periodNumber === PISTOL_H2H_SHOOT_OFF_SET_NUMBER) ||
    ((currentSport === 'ArcheryH2H' || currentSport === 'CompoundArchery') &&
      shootOffSet &&
      periodNumber > shootOffSet)
  ) {
    return i18n.t(`sportsbook.shootOff`);
  }

  if (compactView) {
    return `${i18n.t(`sportsbook.${periodName}`)[0]}${periodNumber}`;
  }

  return `${ordinal(periodNumber, periodName)} ${i18n.t(
    `sportsbook.${periodName}`
  )}`;
}

export function ordinal(num: number, periodName: string | null = null): string {
  const suf = [
    'First',
    'Second',
    'Third',
    'Fourth',
    'Fifth',
    'Sixth',
    'Seventh',
    'Eighth',
    'Ninth',
    'Tenth',
    'Eleventh',
    'Twelfth'
  ];

  if (num <= suf.length) {
    return i18n.t(
      `sportsbook.${
        (periodName ? periodName.toLowerCase() : '') + suf[num - 1]
      }`
    );
  } else {
    return `${num}${i18n.t(
      `sportsbook.${periodName ? periodName.toLowerCase() : ''}DefaultSuffix`
    )}`;
  }
}

export function getGameStatus(
  current_game_state: string,
  current_game_time: string,
  isLive: boolean,
  start_ts: number,
  sport_alias?: string,
  shoot_off_set = 0
): string {
  return isLive
    ? getGameCurrentState(
        { current_game_state, current_game_time },
        sport_alias,
        false,
        shoot_off_set
      )
    : start_ts
    ? dayjs(start_ts * MILLISECONDS_IN_SECOND).format(
        dateFormat({
          date: SpringConfigs.DT.shortDate,
          time: SpringConfigs.DT.time
        })
      )
    : '--';
}

export function checkGameState(item: string): boolean {
  switch (item) {
    case 'Half Time':
      return true;
    case 'timeout':
      return true;
    case 'notstarted':
      return true;
    case 'finished':
      return true;

    default:
      return false;
  }
}

export const getEsportCurrentMapName = (
  additionalInfoData: GameInfoAdditionalData,
  sportAlias?: string
): string => {
  if (!sportAlias || !ESPORTS_ADDITIONAL_DATA[sportAlias]) {
    return '';
  }

  const currentMapRule = ESPORTS_ADDITIONAL_DATA[sportAlias] || {};

  const map = Object.values(additionalInfoData[currentMapRule.maps] || {}).find(
    map =>
      map[currentMapRule.number] === additionalInfoData[currentMapRule.current]
  );

  return map?.[currentMapRule.number]
    ? getMapName(map?.[currentMapRule.number], map?.[currentMapRule.name], true)
    : '';
};

export function ordinalDefault(number: number): string {
  return number === Leaderboards.Place1st
    ? `${number}${i18n.t('sportsbook.st')}`
    : number === Leaderboards.Place2nd
    ? `${number}${i18n.t('sportsbook.nd')}`
    : number == Leaderboards.Place3rd
    ? `${number}${i18n.t('sportsbook.rd')}`
    : `${number}${i18n.t('sportsbook.th')}`;
}

export function getMapName(
  mapNumber: number,
  mapName?: string,
  withoutNumber?: boolean
) {
  return mapName
    ? withoutNumber
      ? mapName
      : `${mapNumber}. ${mapName}`
    : `${ordinal(mapNumber)} ${i18n.t('sportsbook.Map')}`;
}
